import {EmailSenderServiceContract} from 'common/service/sender/EmailSenderServiceContract';
import {TemplateEngine} from 'common/templating/TemplateEngine';
import {TemplateEngineContract} from 'common/templating/TemplateEngineContract';
import {ViewEmailTrackDeliveryContract} from 'common/templating/view/email/ViewEmailTrackDeliveryContract';
import {RecipientContract} from 'common/service/sender/RecipientContract';
import {SenderContract} from 'common/service/sender/SenderContract';
import {Client} from 'node-mailjet';
import {MainException} from 'common/exception/MainException';
import {Retry} from 'common/tool/Retry';
import {Environment} from 'common/tool/Environment';
import {Env} from 'common/tool/Env';
import {Logger} from 'common/tool/Logger';
import {ViewEmailUserNotificationContract} from 'common/templating/view/email/ViewEmailUserNotificationContract';
import {ViewEmailDiscountContract} from 'common/templating/view/email/ViewEmailDiscountContract';
import {asyncForEach} from 'common/util/PromiseUtil';
import {BadParameterException} from 'common/exception/BadParameterException';

export class EmailSenderService implements EmailSenderServiceContract {
    private readonly templateEngine: TemplateEngineContract;
    private readonly mailjet: any;
    private readonly mode: Environment;

    public constructor(apiPublicKey: string, apiSecretKey: string, mode: Environment) {
        this.templateEngine = new TemplateEngine();
        this.mailjet = new Client({apiKey: apiPublicKey, apiSecret: apiSecretKey});
        this.mode = mode;
    }

    public async sendDeliveryDelay(sender: SenderContract, recipient: RecipientContract, data: ViewEmailTrackDeliveryContract): Promise<void> {
        return await Retry.request(async () => {
            if (data.base.copyright === undefined) {
                data.base.copyright = {
                    year: String((new Date()).getUTCFullYear())
                };
            }
            const text: string = await this.templateEngine.render('data/email/template/deliveryDelay', data, 'data/email/element/text');
            const html: string = await this.templateEngine.render('data/email/template/deliveryDelay', data, 'data/email/element/html');

            return await this.sendNotification(sender, [{recipient: recipient, subject: data.base.subject, text: text, html: html}], false);
        }, 0, Logger.error.bind(Logger));
    }

    public async sendTrackDelivery(sender: SenderContract, recipient: RecipientContract, data: ViewEmailTrackDeliveryContract): Promise<void> {
        return await Retry.request(async () => {
            const textContent: string = await this.templateEngine.render('data/email/template/trackDelivery', data, 'data/email/element/text');
            const text: string = await this.templateEngine.render('data/email/element/text/index', {content: textContent, base: data.base});
            const htmlContent: string = await this.templateEngine.render('data/email/template/trackDelivery', data, 'data/email/element/html');
            const html: string = await this.templateEngine.render('data/email/element/html/index', {content: htmlContent, base: data.base});

            return await this.sendNotification(sender, [{recipient: recipient, subject: data.base.subject, text: text, html: html}], false);
        }, 0, Logger.error.bind(Logger));
    }

    public async sendUserNotification(sender: SenderContract, recipient: RecipientContract, data: ViewEmailUserNotificationContract, isCommercial: boolean = true, customId?: string): Promise<void> {
        return await Retry.request(async () => {
            const textContent: string = await this.templateEngine.render('data/email/template/userNotification', data, 'data/email/element/text');
            const text: string = await this.templateEngine.render('data/email/element/text/index', {content: textContent, base: data.base, isCommercial: isCommercial});
            const htmlContent: string = await this.templateEngine.render('data/email/template/userNotification', data, 'data/email/element/html');
            const html: string = await this.templateEngine.render('data/email/element/html/index', {content: htmlContent, base: data.base, isCommercial: isCommercial});

            return await this.sendNotification(sender, [{recipient: recipient, subject: data.base.subject, text: text, html: html}], isCommercial, customId);
        }, 0, Logger.error.bind(Logger));
    }

    public async sendUsersNotifications(sender: SenderContract, messages: {recipient: RecipientContract, data: ViewEmailUserNotificationContract}[],
        isCommercial?: boolean, customId?: string): Promise<void> {
        return await Retry.request(async () => {
            const contents: {recipient: RecipientContract, subject: string, text: string, html: string}[] = [];
            await asyncForEach(messages, async (message) => {
                const textContent: string = await this.templateEngine.render('data/email/template/userNotification', message.data, 'data/email/element/text');
                const text: string = await this.templateEngine.render('data/email/element/text/index', {content: textContent, base: message.data.base, isCommercial: isCommercial});
                const htmlContent: string = await this.templateEngine.render('data/email/template/userNotification', message.data, 'data/email/element/html');
                const html: string = await this.templateEngine.render('data/email/element/html/index', {content: htmlContent, base: message.data.base, isCommercial: isCommercial});
                contents.push({recipient: message.recipient, subject: message.data.base.subject, text: text, html: html});
            });

            return await this.sendNotification(sender, contents, isCommercial, customId);
        }, 0, Logger.error.bind(Logger));
    }

    public async sendDiscount(sender: SenderContract, recipient: RecipientContract, data: ViewEmailDiscountContract, isCommercial: boolean = true, customId?: string): Promise<void> {
        return await Retry.request(async () => {
            const textContent: string = await this.templateEngine.render('data/email/template/discount', data, 'data/email/element/text');
            const text: string = await this.templateEngine.render('data/email/element/text/index', {content: textContent, base: data.base, isCommercial: isCommercial});
            const htmlContent: string = await this.templateEngine.render('data/email/template/discount', data, 'data/email/element/html');
            const html: string = await this.templateEngine.render('data/email/element/html/index', {content: htmlContent, base: data.base, isCommercial: isCommercial});

            return await this.sendNotification(sender, [{recipient: recipient, subject: data.base.subject, text: text, html: html}], isCommercial, customId);
        }, 0, Logger.error.bind(Logger));
    }

    public async sendDiscounts(sender: SenderContract, messages: {recipient: RecipientContract, data: ViewEmailDiscountContract}[], isCommercial: boolean = true, customId?: string): Promise<void> {
        return await Retry.request(async () => {
            const contents: {recipient: RecipientContract, subject: string, text: string, html: string}[] = [];
            await asyncForEach(messages, async (message) => {
                const textContent: string = await this.templateEngine.render('data/email/template/discount', message.data, 'data/email/element/text');
                const text: string = await this.templateEngine.render('data/email/element/text/index', {content: textContent, base: message.data.base, isCommercial: isCommercial});
                const htmlContent: string = await this.templateEngine.render('data/email/template/discount', message.data, 'data/email/element/html');
                const html: string = await this.templateEngine.render('data/email/element/html/index', {content: htmlContent, base: message.data.base, isCommercial: isCommercial});
                contents.push({recipient: message.recipient, subject: message.data.base.subject, text: text, html: html});
            });

            return await this.sendNotification(sender, contents, isCommercial, customId);
        }, 0, Logger.error.bind(Logger));
    }

    // private async sendCampaign(sender: SenderContract, recipients: RecipientContract[], subject: string, text: string, html: string): Promise<void> {
    //     try {
    //         await this.mailjet.post('send').request({
    //             FromEmail: sender.contact,
    //             FromName: sender.name,
    //             Subject: subject,
    //             'Text-part': text,
    //             'Html-part': html,
    //             Recipients: [
    //                 recipients.map((recipient) => {
    //                     return { Name: recipient.name, Email: recipient.contact };
    //                 }),
    //             ],
    //         });
    //
    //         return Promise.resolve();
    //     } catch (error) {
    //         return Promise.reject(new MainException(error.message, Number(error.statusCode)));
    //     }
    // }

    private async sendNotification(sender: SenderContract, contents: {recipient: RecipientContract, subject: string, text: string, html: string}[],
        isCommercial: boolean = false, customId?: string): Promise<void> {
        if (contents.length === 0) {
            throw new BadParameterException('email: missing messages');
        }

        try {
            if (this.mode === Env.TEST) {
                Logger.info('Simulation Mode: Email has been sent', {sender: sender, recipients: contents.map((content) => content.recipient)});
            } else {
                const messages: {From:{Name: string,Email: string},To:[{Name: string,Email: string}], Subject: string, TextPart: string, HTMLPart: string, Priority:0 | 1 | 2, customID: string}[] = [];
                await asyncForEach(contents, async (content) => {
                    if (this.mode === Env.DEVELOPMENT) {
                        content.recipient.contact = 'developer@onylab.com';
                    }
                    messages.push({
                        'From': {
                            'Name': sender.name,
                            'Email': sender.contact
                        },
                        'To': [{
                            'Name': content.recipient.name,
                            'Email': content.recipient.contact
                        }],
                        'Subject': content.subject,
                        'TextPart': content.text,
                        'HTMLPart': content.html,
                        'Priority': isCommercial ? 0 : 2,
                        'customID': customId === undefined ? undefined : String(customId)
                    });
                });
                await this.mailjet.post('send', {'version': 'v3.1'}).request({ Messages: messages });
            }
        } catch (error) {
            throw new MainException(error.message, Number(error.statusCode));
        }
    }
}
